import type {
  AssistantMessage,
  AssistantState,
  AssistantUsage,
  AppNotification,
  AuthTokens,
  Chat,
  Family,
  FamilyFact,
  FamilyMember,
  FamilySpace,
  ImportReport,
  InvitationPreview,
  MeResponse,
  MediaAsset,
  MediaAnalysis,
  Message,
  Person,
  PersonDetail,
  PersonWriteInput,
  PersonFact,
  PersonFactWriteInput,
  PersonPhoto,
  PersonReport,
  Relatives,
  SearchResponse,
  TreeResponse,
} from '@/types/api';
import { enqueueMutation } from './db';
import { API_BASE, isNative, nativeHeaders } from './platform';
import {
  clearTokens,
  getAccessToken,
  getRefreshToken,
  saveTokens,
} from './tokens';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  get isOffline(): boolean {
    return this.status === 0;
  }
}

interface ApiOptions extends RequestInit {
  skipAuth?: boolean;
  skipQueue?: boolean;
}

let refreshPromise: Promise<boolean> | null = null;

async function parseError(response: Response): Promise<ApiError> {
  let message = response.statusText || `HTTP ${response.status}`;
  let details: unknown;
  try {
    const body = (await response.json()) as { message?: string | string[] };
    if (Array.isArray(body.message)) message = body.message.join(', ');
    else if (body.message) message = body.message;
    details = body;
  } catch {
    // Non-JSON error body.
  }
  return new ApiError(response.status, message, details);
}

export async function apiFetch<T>(
  path: string,
  options: ApiOptions = {},
  retried = false,
): Promise<T> {
  const method = (options.method ?? 'GET').toUpperCase();
  const token = getAccessToken();
  const headers = new Headers(options.headers);
  if (!headers.has('Content-Type') && options.body) {
    headers.set('Content-Type', 'application/json');
  }
  if (!options.skipAuth && token) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  for (const [key, value] of Object.entries(nativeHeaders())) {
    headers.set(key, value);
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      ...options,
      method,
      headers,
      credentials: isNative() ? 'omit' : 'include',
    });
  } catch (error) {
    if (method !== 'GET' && !options.skipQueue) {
      await enqueueMutation({
        familyId: familyIdFromPath(path),
        path,
        method: method as 'POST' | 'PATCH' | 'DELETE' | 'PUT',
        body: typeof options.body === 'string' ? options.body : null,
      });
      throw new ApiError(
        0,
        'Нет сети — изменение сохранено и будет отправлено позже',
        error,
      );
    }
    throw new ApiError(0, 'Нет сети', error);
  }

  const authPath = path.startsWith('/auth/') && path !== '/auth/me';
  if (response.status === 401 && !retried && !authPath) {
    const refreshed = await tryRefresh();
    if (refreshed) {
      return apiFetch<T>(path, options, true);
    }
  }

  if (!response.ok) {
    throw await parseError(response);
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export async function tryRefresh(): Promise<boolean> {
  refreshPromise ??= (async () => {
    try {
      if (isNative()) {
        const refreshToken = getRefreshToken();
        if (!refreshToken) return false;
        const tokens = await apiFetch<AuthTokens>(
          '/auth/refresh',
          {
            method: 'POST',
            body: JSON.stringify({ refreshToken }),
            skipAuth: true,
            skipQueue: true,
          },
          true,
        );
        if (!tokens.accessToken || !tokens.refreshToken) return false;
        await saveTokens(tokens);
        return true;
      }

      const response = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: '{}',
      });
      return response.ok;
    } catch {
      return false;
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

function familyIdFromPath(path: string): string | null {
  const match = /^\/families\/([^/]+)/.exec(path);
  return match?.[1] ?? null;
}

export const api = {
  get: <T>(path: string) => apiFetch<T>(path),
  post: <T>(path: string, body?: unknown) =>
    apiFetch<T>(path, {
      method: 'POST',
      body: JSON.stringify(body ?? {}),
    }),
  patch: <T>(path: string, body?: unknown) =>
    apiFetch<T>(path, {
      method: 'PATCH',
      body: JSON.stringify(body ?? {}),
    }),
  del: <T>(path: string) => apiFetch<T>(path, { method: 'DELETE' }),
  deleteAccount: (password: string) =>
    apiFetch<{ ok: boolean }>('/account', {
      method: 'DELETE',
      body: JSON.stringify({ password }),
    }),
  pushStatus: () =>
    api.get<{
      webPush: boolean;
      fcm: boolean;
      apns: boolean;
      nativeSse?: boolean;
    }>('/push/status'),
  createNativePushToken: (deviceName?: string) =>
    api.post<{ token: string; expiresAt: string }>('/native/push/token', {
      deviceName,
    }),
  revokeNativePushToken: (token: string) =>
    apiFetch<{ ok: boolean }>('/native/push/token', {
      method: 'DELETE',
      body: JSON.stringify({ token }),
    }),
  registerDevice: (
    token: string,
    platform: 'android' | 'ios',
    deviceName?: string,
  ) =>
    api.post('/push/devices', { token, platform, deviceName }),
  unregisterDevice: (token: string) =>
    apiFetch<{ ok: boolean }>('/push/devices', {
      method: 'DELETE',
      body: JSON.stringify({ token }),
    }),
  unsubscribePush: (endpoint: string) =>
    apiFetch<{ ok: boolean }>('/push/subscribe', {
      method: 'DELETE',
      body: JSON.stringify({ endpoint }),
    }),
  deleteFamily: (
    familyId: string,
    password: string,
    confirmName: string,
  ) =>
    apiFetch<{ ok: boolean }>(`/families/${familyId}`, {
      method: 'DELETE',
      body: JSON.stringify({ password, confirmName }),
    }),
  put: <T>(path: string, body?: unknown) =>
    apiFetch<T>(path, {
      method: 'PUT',
      body: JSON.stringify(body ?? {}),
    }),
};

interface AuthResult {
  userId?: string;
  accessToken?: string;
  refreshToken?: string;
}

async function finishAuth(result: AuthResult): Promise<void> {
  if (isNative()) {
    if (!result.accessToken || !result.refreshToken) {
      throw new ApiError(500, 'Сервер не вернул токены для мобильного клиента');
    }
    await saveTokens({
      accessToken: result.accessToken,
      refreshToken: result.refreshToken,
    });
  }
}

export const authApi = {
  async login(login: string, password: string): Promise<void> {
    const result = await apiFetch<AuthResult>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ login, password }),
      skipAuth: true,
      skipQueue: true,
    });
    await finishAuth(result);
  },

  async register(input: {
    inviteToken: string;
    username: string;
    email?: string;
    password: string;
    firstName: string;
    lastName: string;
  }): Promise<void> {
    const result = await apiFetch<AuthResult>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(input),
      skipAuth: true,
      skipQueue: true,
    });
    await finishAuth(result);
  },

  forgotPassword: (login: string) =>
    apiFetch<{ ok: boolean }>('/auth/forgot-password', {
      method: 'POST',
      body: JSON.stringify({ login }),
      skipAuth: true,
      skipQueue: true,
    }),

  resetPassword: (token: string, password: string) =>
    apiFetch<{ ok: boolean }>('/auth/reset-password', {
      method: 'POST',
      body: JSON.stringify({ token, password }),
      skipAuth: true,
      skipQueue: true,
    }),

  async logout(): Promise<void> {
    try {
      await apiFetch('/auth/logout', {
        method: 'POST',
        body: JSON.stringify(
          isNative() ? { refreshToken: getRefreshToken() } : {},
        ),
        skipQueue: true,
      });
    } finally {
      await clearTokens();
    }
  },

  me: () => apiFetch<MeResponse>('/auth/me'),
  previewInvitation: (token: string) =>
    apiFetch<InvitationPreview>(`/invitations/${token}`, { skipAuth: true }),
};

export const dataApi = {
  families: () => api.get<Family[]>('/families'),
  createFamily: (name: string) => api.post<Family>('/families', { name }),
  tree: (familyId: string) =>
    api.get<TreeResponse>(`/families/${familyId}/tree`),
  person: (familyId: string, personId: string) =>
    api.get<PersonDetail>(`/families/${familyId}/persons/${personId}`),
  relatives: (familyId: string, personId: string) =>
    api.get<Relatives>(`/families/${familyId}/persons/${personId}/relatives`),
  report: (familyId: string, personId: string) =>
    api.get<PersonReport>(`/families/${familyId}/persons/${personId}/report`),
  createPerson: (familyId: string, payload: PersonWriteInput) =>
    api.post<Person>(`/families/${familyId}/persons`, payload),
  updatePerson: (familyId: string, personId: string, payload: PersonWriteInput) =>
    api.patch<Person>(`/families/${familyId}/persons/${personId}`, payload),
  publication: (familyId: string, personId: string) =>
    api.get<{
      publicPageEnabled: boolean;
      publicSlug: string | null;
      publicPublishedAt: string | null;
      publicPhotoMediaId: string | null;
    }>(`/families/${familyId}/persons/${personId}/publication`),
  updatePublication: (
    familyId: string,
    personId: string,
    payload: { enabled?: boolean; publicPhotoMediaId?: string | null },
  ) =>
    api.patch<{
      publicPageEnabled: boolean;
      publicSlug: string | null;
      publicPublishedAt: string | null;
      publicPhotoMediaId: string | null;
    }>(`/families/${familyId}/persons/${personId}/publication`, payload),
  moderateFact: (
    familyId: string,
    factId: string,
    status: 'CONFIRMED' | 'REJECTED',
  ) =>
    api.patch<PersonFact>(`/families/${familyId}/facts/${factId}/moderate`, {
      status,
    }),
  deletePerson: (familyId: string, personId: string) =>
    api.del(`/families/${familyId}/persons/${personId}`),
  personPhotos: (familyId: string, personId: string) =>
    api.get<PersonPhoto[]>(
      `/families/${familyId}/persons/${personId}/photos`,
    ),
  attachPersonPhoto: (
    familyId: string,
    personId: string,
    mediaId: string,
  ) =>
    api.post<PersonPhoto>(
      `/families/${familyId}/persons/${personId}/photos`,
      { mediaId },
    ),
  removePersonPhoto: (
    familyId: string,
    personId: string,
    mediaId: string,
  ) =>
    api.del(
      `/families/${familyId}/persons/${personId}/photos/${mediaId}`,
    ),
  setPersonAvatar: (
    familyId: string,
    personId: string,
    mediaId: string | null,
  ) =>
    api.patch<{ id: string; avatarMediaId: string | null }>(
      `/families/${familyId}/persons/${personId}/avatar`,
      { mediaId },
    ),
  addFact: (familyId: string, personId: string, payload: PersonFactWriteInput) =>
    api.post<PersonFact>(
      `/families/${familyId}/persons/${personId}/facts`,
      payload,
    ),
  updateFact: (
    familyId: string,
    factId: string,
    payload: PersonFactWriteInput,
  ) =>
    api.patch<PersonFact>(`/families/${familyId}/facts/${factId}`, payload),
  bulkUpdateFactVisibility: (
    familyId: string,
    payload: {
      factIds: string[];
      visibility: 'FAMILY' | 'PRIVATE' | 'RESTRICTED' | 'PUBLIC';
      audienceSpaceIds?: string[];
      audiencePersonIds?: string[];
    },
  ) =>
    api.patch<{ ok: boolean; updated: number }>(
      `/families/${familyId}/facts/bulk-visibility`,
      payload,
    ),
  deleteFact: (familyId: string, factId: string) =>
    api.del(`/families/${familyId}/facts/${factId}`),
  bulkDeleteFacts: (familyId: string, factIds: string[]) =>
    apiFetch<{ ok: boolean; deleted: number }>(
      `/families/${familyId}/facts/bulk`,
      { method: 'DELETE', body: JSON.stringify({ factIds }) },
    ),
  addRelationship: (
    familyId: string,
    payload: { type: string; fromPersonId: string; toPersonId: string },
  ) => api.post(`/families/${familyId}/relationships`, payload),
  renameFamily: (familyId: string, name: string) =>
    api.patch<Family>(`/families/${familyId}`, { name }),
  spaces: (familyId: string) =>
    api.get<FamilySpace[]>(`/families/${familyId}/spaces`),
  createSpace: (
    familyId: string,
    payload: {
      name: string;
      description?: string;
      color?: string;
      visibility?: 'PUBLIC' | 'PRIVATE';
    },
  ) => api.post<FamilySpace>(`/families/${familyId}/spaces`, payload),
  updateSpace: (
    familyId: string,
    spaceId: string,
    payload: {
      name?: string;
      description?: string;
      color?: string;
      visibility?: 'PUBLIC' | 'PRIVATE';
    },
  ) => api.patch<FamilySpace>(`/families/${familyId}/spaces/${spaceId}`, payload),
  deleteSpace: (familyId: string, spaceId: string) =>
    api.del(`/families/${familyId}/spaces/${spaceId}`),
  assignSpacePersons: (familyId: string, spaceId: string, personIds: string[]) =>
    api.post<FamilySpace>(`/families/${familyId}/spaces/${spaceId}/persons`, {
      personIds,
    }),
  unassignSpacePerson: (familyId: string, spaceId: string, personId: string) =>
    api.del(`/families/${familyId}/spaces/${spaceId}/persons/${personId}`),
  setSpaceBranches: (
    familyId: string,
    spaceId: string,
    branchRootIds: string[],
  ) =>
    api.put<FamilySpace>(
      `/families/${familyId}/spaces/${spaceId}/branches`,
      { branchRootIds },
    ),
  personSpaces: (familyId: string, personId: string) =>
    api.get<FamilySpace[]>(
      `/families/${familyId}/persons/${personId}/spaces`,
    ),
  setPersonSpaces: (familyId: string, personId: string, spaceIds: string[]) =>
    api.put<{ spaceIds: string[] }>(
      `/families/${familyId}/persons/${personId}/spaces`,
      { spaceIds },
    ),
  members: (familyId: string) =>
    api.get<FamilyMember[]>(`/families/${familyId}/members`),
  familyFacts: (
    familyId: string,
    limit = 50,
    scope: 'mine' | 'all' = 'all',
    sort: 'date' | 'updated' = 'date',
  ) =>
    api.get<FamilyFact[]>(
      `/families/${familyId}/facts?limit=${limit}&scope=${scope}&sort=${sort}`,
    ),
  search: (familyId: string, query: string) =>
    api.get<SearchResponse>(
      `/families/${familyId}/search?q=${encodeURIComponent(query)}`,
    ),
  setMemberPerson: (
    familyId: string,
    userId: string,
    personId: string | null,
  ) =>
    api.patch<FamilyMember>(
      `/families/${familyId}/members/${userId}/person`,
      { personId },
    ),
  assistantState: (familyId: string) =>
    api.get<AssistantState>(`/families/${familyId}/assistant`),
  assistantMessages: (familyId: string) =>
    api.get<AssistantMessage[]>(`/families/${familyId}/assistant/messages`),
  assistantStatus: (familyId: string) =>
    api.get<{ busy: boolean; state: string }>(
      `/families/${familyId}/assistant/status`,
    ),
  assistantSend: (
    familyId: string,
    text: string,
    attachmentIds: string[] = [],
  ) =>
    api.post<{ ok: boolean }>(`/families/${familyId}/assistant/messages`, {
      text,
      attachmentIds,
    }),
  assistantAbort: (familyId: string) =>
    api.post<{ ok: boolean }>(`/families/${familyId}/assistant/abort`, {}),
  assistantApplyDraft: (familyId: string, draftId: string) =>
    api.post<{ ok: boolean; entityId: string | null; viewPath: string | null }>(
      `/families/${familyId}/assistant/drafts/${draftId}/apply`,
      {},
    ),
  assistantCancelDraft: (familyId: string, draftId: string) =>
    api.post<{ ok: boolean }>(
      `/families/${familyId}/assistant/drafts/${draftId}/cancel`,
      {},
    ),
  assistantUsage: (familyId: string, from?: string, to?: string) => {
    const qs = new URLSearchParams();
    if (from) qs.set('from', from);
    if (to) qs.set('to', to);
    return api.get<AssistantUsage>(
      `/families/${familyId}/assistant/usage${qs.size ? `?${qs}` : ''}`,
    );
  },
  resetMemberPassword: (familyId: string, userId: string) =>
    api.post<{ resetUrl: string; expiresAt: string; emailSent: boolean }>(
      `/families/${familyId}/members/${userId}/password-reset`,
      {},
    ),
  invitation: (familyId: string, personId: string, email?: string) =>
    api.post<{ inviteUrl: string; token: string; emailSent: boolean }>(
      `/families/${familyId}/invitations`,
      { personId, email },
    ),
  chats: (familyId: string) =>
    api.get<Chat[]>(`/families/${familyId}/chats`),
  chatUnread: (familyId: string) =>
    api.get<{ unread: number }>(`/families/${familyId}/chats/unread`),
  chat: (familyId: string, chatId: string) =>
    api.get<Chat>(`/families/${familyId}/chats/${chatId}`),
  createChat: (
    familyId: string,
    payload: { type?: string; title?: string; memberUserIds?: string[] },
  ) => api.post<Chat>(`/families/${familyId}/chats`, payload),
  deleteChat: (familyId: string, chatId: string) =>
    api.del(`/families/${familyId}/chats/${chatId}`),
  messages: (familyId: string, chatId: string) =>
    api.get<Message[]>(`/families/${familyId}/chats/${chatId}/messages`),
  sendMessage: (
    familyId: string,
    chatId: string,
    body: string,
    clientId: string = crypto.randomUUID(),
    attachments: string[] = [],
  ) =>
    api.post<Message>(`/families/${familyId}/chats/${chatId}/messages`, {
      body,
      clientId,
      attachments,
    }),
  updateMessage: (
    familyId: string,
    chatId: string,
    messageId: string,
    body: string,
  ) =>
    api.patch<Message>(
      `/families/${familyId}/chats/${chatId}/messages/${messageId}`,
      { body },
    ),
  deleteMessage: (familyId: string, chatId: string, messageId: string) =>
    api.del(
      `/families/${familyId}/chats/${chatId}/messages/${messageId}`,
    ),
  addChatMembers: (
    familyId: string,
    chatId: string,
    memberUserIds: string[],
  ) =>
    api.post<Chat>(`/families/${familyId}/chats/${chatId}/members`, {
      memberUserIds,
    }),
  markRead: (familyId: string, chatId: string, messageId: string) =>
    api.post(`/families/${familyId}/chats/${chatId}/read`, { messageId }),
  notifications: () =>
    api.get<{ items: AppNotification[]; unread: number }>('/notifications'),
  markNotificationRead: (id: string) =>
    api.post(`/notifications/${id}/read`),
  markAllNotificationsRead: () =>
    api.post('/notifications/read-all'),
  uploadMedia: (familyId: string, file: File) =>
    uploadMedia(familyId, file),
  media: (familyId: string) =>
    api.get<MediaAsset[]>(`/families/${familyId}/media`),
  mediaAnalysis: (familyId: string, mediaId: string, refresh = false) =>
    api.get<MediaAnalysis>(
      `/families/${familyId}/media/${mediaId}/analysis${refresh ? '?refresh=1' : ''}`,
    ),
  confirmMediaFace: (
    familyId: string,
    mediaId: string,
    faceId: string,
    personId: string,
  ) =>
    api.post<{ ok: boolean }>(
      `/families/${familyId}/media/${mediaId}/faces/${faceId}/confirm`,
      { personId },
    ),
  rejectMediaFace: (familyId: string, mediaId: string, faceId: string) =>
    api.post<{ ok: boolean }>(
      `/families/${familyId}/media/${mediaId}/faces/${faceId}/reject`,
      {},
    ),
  importTree: (
    familyId: string,
    format: 'gedcom' | 'json' | 'csv',
    content: string,
  ) => api.post<ImportReport>(`/families/${familyId}/import/${format}`, { content }),
};

async function authHeaders(): Promise<Headers> {
  const headers = new Headers(nativeHeaders());
  const token = getAccessToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);
  return headers;
}

export function mediaUrl(familyId: string, mediaId: string): string {
  return `${API_BASE}/families/${familyId}/media/${mediaId}`;
}

export async function uploadMedia(
  familyId: string,
  file: File,
): Promise<MediaAsset> {
  const form = new FormData();
  form.append('file', file);
  const response = await fetch(`${API_BASE}/families/${familyId}/media`, {
    method: 'POST',
    headers: await authHeaders(),
    credentials: isNative() ? 'omit' : 'include',
    body: form,
  });
  if (!response.ok) throw await parseError(response);
  return (await response.json()) as MediaAsset;
}

export async function fetchMediaBlob(
  familyId: string,
  mediaId: string,
): Promise<Blob> {
  const response = await fetch(mediaUrl(familyId, mediaId), {
    headers: await authHeaders(),
    credentials: isNative() ? 'omit' : 'include',
  });
  if (!response.ok) throw await parseError(response);
  return response.blob();
}

export async function downloadExport(
  familyId: string,
  format: 'gedcom' | 'json' | 'csv',
): Promise<void> {
  const token = getAccessToken();
  const headers = new Headers(nativeHeaders());
  if (token) headers.set('Authorization', `Bearer ${token}`);
  const response = await fetch(`${API_BASE}/families/${familyId}/export/${format}`, {
    headers,
    credentials: isNative() ? 'omit' : 'include',
  });
  if (!response.ok) throw await parseError(response);
  const blob = await response.blob();
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `heritage.${format === 'gedcom' ? 'ged' : format}`;
  link.click();
  URL.revokeObjectURL(url);
}

export async function transcribeAudio(
  familyId: string,
  wav: Blob,
  language: 'ru' | 'en',
): Promise<string> {
  const form = new FormData();
  form.append('audio', wav, 'audio.wav');
  form.append('language', language);
  const headers = new Headers(nativeHeaders());
  const token = getAccessToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);
  const response = await fetch(
    `${API_BASE}/families/${familyId}/assistant/transcribe`,
    {
      method: 'POST',
      headers,
      credentials: isNative() ? 'omit' : 'include',
      body: form,
    },
  );
  if (!response.ok) throw await parseError(response);
  const data = (await response.json()) as { text?: string };
  return (data.text ?? '').trim();
}
